import type React from 'react';
import { createContext, lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuthContext } from '../auth/AuthContext';
import type { FormConfig } from '../config/types';
import { useFormData } from '../hooks/useFormData';
import type { FetchFormResponse } from '../service/types';
import { resolveSelectedFormId } from '../utils/form-id';
import {
  replaceReportingPeriodTokensInConfig,
  resolveReportPeriodDefaultValue,
} from '../utils/reporting-period';

interface FormConfigContextType {
  config: FormConfig;
  isLoading: boolean;
  error: Error | null;
  portalComponent: React.ReactNode;
  tenant: string;
}

export const FormConfigContext = createContext<FormConfigContextType>({
  config: {} as FormConfig,
  portalComponent: null,
  isLoading: true,
  error: null,
  tenant: '',
});

interface FormConfigProviderProps {
  children: React.ReactNode;
}

export const loadFormConfig = async (configName?: string): Promise<FormConfig> => {
  if (!configName) {
    return {} as FormConfig;
  }
  const module = await import(`../config/configs/${configName}`);
  return module.default as FormConfig;
};

const toVersionedConfigUrl = (tenantName: string, formVersion: number): string => {
  const basePath = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  return `${basePath}/configs/${tenantName}/form-config-v${formVersion}.json`;
};

const isFormConfig = (value: unknown): value is FormConfig => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.title === 'string' && Array.isArray(candidate.sections);
};

const loadVersionedFormConfig = async (
  tenantName: string,
  formVersion: number,
): Promise<FormConfig> => {
  const configUrl = toVersionedConfigUrl(tenantName, formVersion);
  const response = await fetch(configUrl, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Unable to load form config v${formVersion} for tenant ${tenantName}`);
  }

  const configJson: unknown = await response.json();
  if (!isFormConfig(configJson)) {
    throw new Error(`Invalid form config payload for tenant ${tenantName} version ${formVersion}`);
  }

  return configJson;
};

const toPositiveInteger = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) return value;
  if (typeof value !== 'string') return undefined;
  const parsed = Number.parseInt(value, 10);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
};

const resolveFormVersion = (response: FetchFormResponse | undefined): number | undefined => {
  return toPositiveInteger(response?.formVersion ?? response?.form_version);
};

export const FormConfigProvider: React.FC<FormConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<FormConfig>({} as FormConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { tenant } = useParams<{ tenant: string }>();
  const location = useLocation();
  const { isAuthenticated } = useAuthContext();
  const { data, isError: hasFormDataError, isLoading: isFormDataLoading } = useFormData();
  const configName = tenant;
  const searchParams = new URLSearchParams(location.search || '');
  const myFormsParam = searchParams.get('my_forms') || searchParams.get('myForms') || '';
  const forceMyFormsFromQueryParam =
    ['1', 'true', 'yes', 'y'].includes(myFormsParam.trim().toLowerCase()) ||
    (tenant === 'scs' && /^\/scs\/moderation\/?$/.test(location.pathname));
  const selectedFormId = forceMyFormsFromQueryParam
    ? undefined
    : resolveSelectedFormId(tenant, location.search || '');
  const formVersion = resolveFormVersion(data);
  const isPublicTenantRoute = useMemo(
    () =>
      /^(\/[^/]+)?\/(signin|reset-password-email|reset-password|enter-app-mfa-code|set-new-password|cookies|accessibility|privacy|terms-of-use|error)\/?$/i.test(
        location.pathname || '',
      ),
    [location.pathname],
  );
  const shouldWaitForVersionedConfig =
    Boolean(selectedFormId) &&
    isAuthenticated &&
    !isPublicTenantRoute &&
    formVersion === undefined &&
    !hasFormDataError &&
    isFormDataLoading;

  const PortalComponent = lazy(() => import(`../screens/Portals/${configName}`));

  useEffect(() => {
    const loadConfig = async () => {
      if (!configName) {
        setConfig({} as FormConfig);
        setError(null);
        setIsLoading(false);
        return;
      }
      if (shouldWaitForVersionedConfig) {
        setIsLoading(true);
        return;
      }

      try {
        setIsLoading(true);
        const baseConfig = await loadFormConfig(configName);
        const loadedConfig =
          formVersion !== undefined
            ? {
                ...baseConfig,
                ...(await loadVersionedFormConfig(configName, formVersion)),
              }
            : baseConfig;
        setConfig(
          replaceReportingPeriodTokensInConfig(
            loadedConfig,
            resolveReportPeriodDefaultValue(baseConfig),
          ),
        );
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load form configuration'));
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, [configName, formVersion, shouldWaitForVersionedConfig]);

  return (
    <FormConfigContext.Provider
      value={{
        config,
        isLoading,
        error,
        tenant: tenant || '',
        portalComponent: (
          <Suspense fallback="Loading...">
            <PortalComponent />
          </Suspense>
        ),
      }}
    >
      {children}
    </FormConfigContext.Provider>
  );
};
